import React, {
  forwardRef,
  useEffect,
  useRef,
} from 'react';
import './index.scss';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteRenderGetTagProps,
} from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Paper,
} from '@mui/material';
import { TAutoCompleteMultiProps } from '../controllerTypes';
import {
  FieldPath,
  FieldValues,
  useController,
} from 'react-hook-form';
import {
  TAutoCompleteOptionValue,
  TAutoCompleteOptions,
} from '../../../../src/utils/types';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function MultiSelect<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required,
  options = [],
  placeholder = `Select ${label?.toLowerCase()}`,
  error,
  disabled,
  getOptionDisabled = () => false,
  showMultiSelectActions = true,
  showSelectAll = true,
  maxCount = false,
  limitTags = 1,
}: TAutoCompleteMultiProps<TFieldValues, TFieldName>) {
  const { field } = useController({
    name,
    control,
    disabled,
  });
  const getSelectedValue = () => {
    return options?.filter((e) => {
      return field?.value?.includes(e.value);
    });
  };
  const selectedValue = getSelectedValue();

  const [checkAll, setCheckAll] = React.useState(false);

  const handleToggleSelectAll = () => {
    setCheckAll((prev) => {
      if (!prev)
        field.onChange(
          options?.map((element) => element?.value)
        );
      else field.onChange([]);
      return !prev;
    });
  };
  const renderTagsFunction = (
    value: TAutoCompleteOptions[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => {
    const numTags = value.length;

    return (
      <>
        {value
          .slice(0, limitTags)
          .map(
            (
              option: TAutoCompleteOptions,
              index: number
            ) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={option?.label}
                className="moreContainer"
              />
            )
          )}

        {numTags > limitTags && (
          <Chip
            label={
              numTags > limitTags &&
              ` +${numTags - limitTags} more...`
            }
            style={{
              fontSize: 11,

              backgroundColor: 'rgba(0, 143, 237, 0.1)',
            }}
          />
        )}
      </>
    );
  };

  const onChangeFunction = (
    selectedOption: TAutoCompleteOptions[],
    reason: string,
    details:
      | AutocompleteChangeDetails<TAutoCompleteOptions>
      | undefined
  ) => {
    if (reason === 'clear' || reason === 'removeOption')
      setCheckAll(false);
    if (
      reason === 'selectOption' &&
      selectedOption.length === options?.length
    )
      setCheckAll(true);

    if (reason === 'clear') {
      field.onChange(
        selectedOption?.map((element) => element?.value)
      );
    } else if (reason === 'removeOption') {
      field.onChange(
        field.value?.filter(
          (value: TAutoCompleteOptionValue) =>
            value !== details?.option?.value
        )
      );
    } else if (
      reason === 'selectOption' &&
      details &&
      maxCount
    ) {
      const currValues = [...field.value];
      if (currValues.length < 5) {
        currValues.push(details?.option.value);
        field.onChange(currValues);
      }
    } else if (reason === 'selectOption' && details) {
      const currValues = [...field.value];
      currValues.push(details?.option.value);
      field.onChange(currValues);
    }
  };

  const PaperComponentFunction = (
    paperProps: React.HTMLAttributes<HTMLElement>
  ) => {
    const { children, ...restPaperProps } = paperProps;
    return (
      <Paper {...restPaperProps}>
        {showMultiSelectActions && (
          <Box
            onMouseDown={(e) => e.preventDefault()}
            pl={1.5}
            paddingTop={1}
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}>
            {showSelectAll && (
              <Button
                sx={{
                  color: 'rgba(0, 143, 237, 1)',
                  fontSize: 13,
                  textTransform: 'none',
                }}
                onClick={(e) => {
                  options?.map((element) => element?.value);
                  e.preventDefault(); // prevent blur

                  handleToggleSelectAll();
                }}>
                Select all
              </Button>
            )}
            {!!selectedValue?.length && (
              <Button
                sx={{
                  color: 'rgba(0, 143, 237, 1)',
                  fontSize: 13,
                  textTransform: 'none',
                }}
                onClick={(e) => {
                  e.preventDefault();

                  field.onChange([]);
                  setCheckAll(false);
                }}>
                Clear all
              </Button>
            )}
          </Box>
        )}

        {children}
      </Paper>
    );
  };

  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollPosition = useRef(0);

  // Custom Listbox component to retain scroll position
  const ListboxComponent = forwardRef(
    function CustomListbox(
      props: React.HTMLAttributes<HTMLElement>,
      ref: React.Ref<HTMLUListElement>
    ) {
      // Prevents re-setting scroll position unless there's a change in the options list
      useEffect(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop =
            scrollPosition.current;
        }
      }, [props.children]);

      const handleScroll = (
        event: React.UIEvent<HTMLUListElement>
      ) => {
        scrollPosition.current =
          event.currentTarget.scrollTop;
      };

      return (
        <ul
          {...props}
          ref={(instance) => {
            listboxRef.current = instance;
            if (typeof ref === 'function') {
              ref(instance);
            } else if (ref) {
              (
                ref as React.MutableRefObject<HTMLUListElement | null>
              ).current = instance;
            }
          }}
          onScroll={handleScroll}
          style={{ overflow: 'auto', maxHeight: '15rem' }}
        />
      );
    }
  );

  return (
    <FormControl fullWidth className="h-28">
      {label && (
        <label className="form-field-label mt-[10px]">
          {label}
          {required && <i className="required">*</i>}
        </label>
      )}
      <Autocomplete
        multiple
        size="small"
        id="checkboxes-tags-demo"
        className="custom-autocompleteSelect multiselect"
        limitTags={limitTags}
        options={options}
        {...field}
        disableCloseOnSelect
        getOptionDisabled={getOptionDisabled}
        ListboxComponent={ListboxComponent}
        renderTags={(value, getTagProps) =>
          renderTagsFunction(value, getTagProps)
        }
        getOptionLabel={(option: TAutoCompleteOptions) =>
          option?.label
        }
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={
              option?.key
                ? option?.key
                : option?.value?.toString()
            }
            style={{
              fontSize: '13px',
              fontFamily: 'Poppins',
            }}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 5 }}
              checked={selected || checkAll}
            />
            {option?.label}
          </li>
        )}
        value={selectedValue}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              field?.value?.length > 0 ? '' : placeholder
            }
          />
        )}
        onChange={(_e, selectedOption, reason, details) => {
          onChangeFunction(selectedOption, reason, details);
        }}
        PaperComponent={(paperProps) =>
          PaperComponentFunction(paperProps)
        }
      />
      <p
        className={`form-field-error ${
          error ? 'visible' : ''
        }`}>
        {error?.toString()}
      </p>
    </FormControl>
  );
}
